import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import {
  MatSort,
  MatSortModule,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { ButtonModule } from '@openng/optimus-ui/button';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { ColumnOptionBase } from '../table/table';

export type MatColumnOption<T> =
  | MatToggleColumnOption<T>
  | MatTextColumnOption<T>
  | MatTemplateColumnOption<T>;
@Component({
  selector: 'core-material-table',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    ButtonModule,
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'),
      ),
    ]),
  ],
  template: `
    <table
      class="mat-elevation-z8"
      [ngClass]="[
        _tableOption.tableClass,
        columnTemplateOption.length ? 'visible' : 'hidden',
      ]"
      [matSortDisabled]="sortDisabled"
      [trackBy]="trackBy"
      [multiTemplateDataRows]="_tableOption.expandTemplate ? true : false"
      mat-table
      matSort
      aria-label="Elements"
    >
      @for (column of columnTemplateOption; track column.field) {
        <ng-container matColumnDef="{{ column.field }}">
          <th
            *matHeaderCellDef
            [ngClass]="column.option.headerClass"
            [disabled]="column.disabled"
            mat-header-cell
            mat-sort-header
          >
            @if (column.headerTemplate) {
              <ng-container
                [ngTemplateOutlet]="column.headerTemplate"
                [ngTemplateOutletContext]="{ option: column.option }"
              >
              </ng-container>
            }
            @if (column.header) {
              <span class="table-header-text">{{ column.header }}</span>
            }
          </th>
          <td
            *matCellDef="let row; let index = index"
            [ngClass]="column.option.dataClass"
            mat-cell
          >
            @switch (column.rowTemplate) {
              @case ('tplTemplateColumn') {
                <ng-container
                  [ngTemplateOutlet]="setColumnTemplate(column.option)"
                  [ngTemplateOutletContext]="{
                    option: column.option,
                    row: row,
                    index: index,
                  }"
                >
                </ng-container>
              }
              @case ('tplToggleColumn') {
                <ng-container
                  [ngTemplateOutlet]="tplToggleColumn"
                  [ngTemplateOutletContext]="{
                    option: column.option,
                    row: row,
                  }"
                ></ng-container>
              }
              @case ('tplTextColumn') {
                <ng-container
                  [ngTemplateOutlet]="tplTextColumn"
                  [ngTemplateOutletContext]="{
                    option: column.option,
                    row: row,
                  }"
                ></ng-container>
              }
            }
          </td>
        </ng-container>
      }

      <tr
        *matHeaderRowDef="displayFields"
        [ngClass]="_tableOption.tableHeaderClass"
        mat-header-row
      ></tr>
      <tr
        *matRowDef="let row; columns: displayFields"
        [ngClass]="
          _tableOption.selectRow ? 'hover:bg-hover hover:cursor-pointer' : ''
        "
        (click)="onClick(row, $event)"
        mat-row
      ></tr>

      @if (_tableOption.expandTemplate) {
        <ng-container matColumnDef="expand">
          <th class="w-12" *matHeaderCellDef mat-header-cell>&nbsp;</th>
          <td class="p-0" *matCellDef="let row" mat-cell>
            <p-button
              (click)="
                expandedRow = expandedRow === row ? null : row;
                $event.stopPropagation()
              "
              [text]="true"
              [rounded]="true"
              severity="secondary"
            >
              @if (expandedRow !== row) {
                <mat-icon>keyboard_arrow_down</mat-icon>
              }
              @if (expandedRow === row) {
                <mat-icon>keyboard_arrow_up</mat-icon>
              }
            </p-button>
          </td>
        </ng-container>
        <ng-container matColumnDef="expandedDetail">
          <td
            class="mat-table-expand-container"
            *matCellDef="let row"
            [attr.colspan]="displayFields.length"
            mat-cell
          >
            <div
              [@detailExpand]="row === expandedRow ? 'expanded' : 'collapsed'"
            >
              <ng-container
                [ngTemplateOutlet]="_tableOption.expandTemplate"
                [ngTemplateOutletContext]="{ option: _tableOption, row: row }"
              ></ng-container>
            </div>
          </td>
        </ng-container>
        <tr
          class="h-0"
          *matRowDef="let row; columns: ['expandedDetail']"
          mat-row
        ></tr>
      }
    </table>

    @if (_tableOption.showPaginator) {
      <mat-paginator
        #paginator
        [length]="totalCount$ | async"
        [pageIndex]="0"
        [pageSize]="_tableOption.pageSize"
        [pageSizeOptions]="[5, 10, 20]"
        aria-label="Select page"
      >
      </mat-paginator>
    } @else {
      <!-- mysterious workaround, since angular 19 the grid won't render initially without this (until a mouse hover)
         - adding a completely unlinked paginator has a mysterious effect of fixing the issue
       -->
      <mat-paginator class="hidden" [pageSizeOptions]="[1, 2]"></mat-paginator>
    }
    <ng-template #tplToggleColumn let-option="option" let-row="row">
      @for (column of option.columns; track column.field) {
        <mat-icon>{{
          row[column.field] ? column.icon : (column.iconDisabled ?? '')
        }}</mat-icon>
      }
    </ng-template>
    <ng-template #tplTextColumn let-option="option" let-row="row">
      @if (option.dateFormat) {
        <span>{{
          row[option.field] | date: option.dateFormat : option.timezone
        }}</span>
      } @else if (option.decimalPlaces) {
        <span>{{
          row[option.field] | number: '1.0-' + option.decimalPlaces
        }}</span>
      } @else {
        <span>{{ row[option.field] }}</span>
      }
    </ng-template>
  `,
  styles: [
    `
      .border-none-table {
        @apply border-none;
        @apply bg-2;
      }
      .border-none-table th {
        @apply border-none;
        @apply text-white/50;
        @apply text-sm;
        @apply font-bold;
      }
      .border-none-table tr,
      .border-none-table td {
        @apply border-none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialTableComponent<T extends object> implements AfterViewInit {
  @ViewChild('paginator') protected paginator!: MatPaginator;
  @ViewChild(MatSort) protected sort!: MatSort;
  @ViewChild(MatTable) protected table!: MatTable<T>;

  protected _defaultTableOption: MatTableOption = {
    showPaginator: true,
    tableClass: '',
    tableHeaderClass: '',
    selectRow: true,
    pageSize: 5,
  };

  @Output()
  public rowClick = new EventEmitter<{ row: T; index: number }>();
  @Output()
  public dataChanged = new EventEmitter<readonly T[]>();

  @Input()
  public set tableOption(option: MatTableOption) {
    this._tableOption = Object.assign(option, this._defaultTableOption, {
      ...option,
    });
  }
  public get tableOption(): MatTableOption {
    return this._tableOption;
  }
  protected _tableOption: MatTableOption = this._defaultTableOption;

  @Input()
  public data$: Observable<T[]> = EMPTY;
  private _data: T[] = [];

  @Input()
  public trackBy: TrackByFunction<T> = (index, item) => item;

  @Input()
  public sortDisabled = false;

  @Input()
  public defaultSort?: { field: keyof T; direction: SortDirection };

  @Input()
  public expandedRow: T | null = null;

  @Input()
  public set displayedColumns(options: MatColumnOption<T>[]) {
    if (!options) return;
    this.columnTemplateOption = options.map(
      (i: MatColumnOption<T>, index: number): ColumnTemplateOption<T> => {
        const result: ColumnTemplateOption<T> = {
          option: i,
          rowTemplate: '',
          field: '',
          disabled: i.disabled ?? false,
        };
        if (i.header && typeof i.header == 'string') {
          result.header = i.header;
        } else if (typeof i.header == 'object') {
          result.headerTemplate = i.header;
        }
        let disabled = false;
        if (isMatTemplateColumnOption<T>(i)) {
          result.rowTemplate = 'tplTemplateColumn';
          result.field = index.toString();
          disabled = !i.getFieldValue;
        } else if (isMatToggleColumnOption<T>(i)) {
          result.rowTemplate = 'tplToggleColumn';
          result.field = index.toString();
          disabled = !i.getFieldValue;
        } else if (isMatTextColumnOption<T>(i)) {
          result.rowTemplate = 'tplTextColumn';
          result.field = i.field.toString();
        }

        if (!result.disabled && disabled) {
          result.disabled = disabled;
        }
        return result;
      },
    );
    this.displayFields = this.columnTemplateOption.map(i => i.field);
    if (this._tableOption.expandTemplate) {
      this.displayFields.push('expand');
    }
    this.filterableColumnOptions = this.columnTemplateOption.filter(
      i => i.option.filterable,
    );
  }
  protected columnTemplateOption: ColumnTemplateOption<T>[] = [];
  protected displayFields: string[] = [];
  protected filterableColumnOptions: ColumnTemplateOption<T>[] = [];
  protected filterChange = new BehaviorSubject('');

  public get filter(): string {
    return this.filterChange.value;
  }

  public set filter(filter: string) {
    this.filterChange.next(filter);
  }

  public totalCount$: Observable<number> = EMPTY;
  public displayCount$: Observable<number> = EMPTY;

  public ngAfterViewInit(): void {
    this.sort.disableClear = true;
    if (this.defaultSort) {
      this.sort.active = this.defaultSort.field.toString();
      this.sort.direction = this.defaultSort.direction;
    }
    this.totalCount$ = this.data$.pipe(
      map(data => {
        this._data = data ?? [];
        return data.length;
      }),
    );
    this.table.dataSource = combineLatest([
      this.data$,
      this.sort.sortChange.pipe(
        startWith<Sort>({
          active: this.sort.active,
          direction: this.sort.direction,
        }),
      ),
      this.paginator?.page.pipe(
        startWith<PageEvent>({
          pageIndex: 0,
          pageSize: this.paginator.pageSize,
          length: 0,
        }),
      ) ?? new BehaviorSubject(false),
      this.filterChange,
    ]).pipe(
      map(
        ([data, sort, paginator, filter]) =>
          [this.sortData(this.filterData(data, filter), sort), paginator] as [
            T[],
            PageEvent,
          ],
      ),
      map(([data, paginator]) => {
        return paginator ? this.pageData(paginator, data) : data;
      }),
      tap(data => {
        this.dataChanged.emit(data);
      }),
    );
    this.displayCount$ = this.table.dataSource.pipe(map(data => data.length));
  }

  protected setColumnTemplate(
    option: MatColumnOption<T>,
  ): TemplateRef<unknown> {
    return (option as MatTemplateColumnOption<T>).rowTemplate;
  }

  private filterData(data: T[], filter: string): T[] {
    if (!filter || this.filterableColumnOptions.length == 0) return data;
    return data.filter((row: T) => {
      let searchStr = '';
      for (const columnOption of this.filterableColumnOptions) {
        if (isMatTextColumnOption<T>(columnOption.option)) {
          searchStr += row[columnOption.field as keyof T];
        } else if (columnOption.option.getFieldValue) {
          searchStr += columnOption.option.getFieldValue(row);
        }
      }
      return searchStr.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
    });
  }

  private sortData(data: T[], sort: Sort): T[] {
    if (!sort.active) {
      return [...data];
    }

    return [...data].sort((a, b) => {
      const sortField = sort.active;
      const isNumber = !isNaN(Number(sortField));
      let _a, _b;
      if (isNumber) {
        const columnOption = <ColumnOptionBase<T>>(
          this.columnTemplateOption.find(i => i.field == sortField)?.option
        );
        if (!columnOption?.getFieldValue) {
          return 0;
        }
        _a = columnOption.getFieldValue(a);
        _b = columnOption.getFieldValue(b);
      } else {
        const sortKey = sortField as keyof T;
        const lookup = (source: T, key: keyof T) =>
          typeof source[key] == 'string'
            ? (<string>source[key]).toLowerCase()
            : source[key];
        _a = lookup(a, sortKey);
        _b = lookup(b, sortKey);
      }
      if (_a == _b) return 0;

      const sortDirection = sort.direction == 'asc' ? 1 : -1;
      if (_a > _b) return sortDirection;
      return -sortDirection;
    });
  }

  private pageData(paginator: PageEvent, data: T[]) {
    const startIndex = paginator.pageIndex * paginator.pageSize;
    const page = data.splice(startIndex, paginator.pageSize);
    return page;
  }

  protected onClick(row: T, event: MouseEvent) {
    if (this._tableOption.expandTemplate) {
      this.expandedRow = this.expandedRow === row ? null : row;
    }
    const element = event.target as Node;
    if (element?.parentElement?.tagName == 'BUTTON') {
      event.preventDefault();
      return;
    }
    const index = this._data.indexOf(row);
    this.rowClick.next({ row, index });
  }
}

export interface MatTableOption {
  showPaginator?: boolean;
  tableClass?: string;
  tableHeaderClass?: string;
  selectRow?: boolean;
  pageSize?: number;
  expandTemplate?: TemplateRef<unknown>;
}

interface ColumnTemplateOption<T> {
  field: string;
  disabled: boolean;
  rowTemplate: string;
  header?: string;
  headerTemplate?: TemplateRef<unknown>;
  option: MatColumnOption<T>;
}

export interface MatToggleFieldDefinition<T> {
  field: keyof T;
  icon: string;
  iconDisabled?: string;
}

export interface MatTextColumnOption<T> extends ColumnOptionBase<T> {
  field: keyof T;
  dateFormat?: string;
  timezone?: string;
  decimalPlaces?: number;
}

export interface MatToggleColumnOption<T> extends ColumnOptionBase<T> {
  columns: MatToggleFieldDefinition<T>[];
}

export interface MatTemplateColumnOption<T> extends ColumnOptionBase<T> {
  rowTemplate: TemplateRef<unknown>;
}

export function isMatTemplateColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is MatTemplateColumnOption<T> {
  return (option as MatTemplateColumnOption<T>).rowTemplate !== undefined;
}

export function isMatToggleColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is MatToggleColumnOption<T> {
  return (option as MatToggleColumnOption<T>).columns !== undefined;
}

export function isMatTextColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is MatTextColumnOption<T> {
  return (option as MatTextColumnOption<T>).field !== undefined;
}
